import { Document, Image, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import numWords from "num-words";

const styles = StyleSheet.create({
  page: { padding: 24, fontSize: 9 },
  table: { borderLeft: 1, borderColor: "#000", marginBottom: 0 },
  tableFirst: { borderTop: 1 },
  logoContainer: {
    width: "30%",
    justifyContent: "center",
    alignItems: "center",
    borderBottomWidth: 1,
    borderRightWidth: 1,
  },
  logo: {
    width: 60,
    height: 60,
    objectFit: "contain",
  },
  row: { flexDirection: "row" },
  cell: {
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000",
    padding: 4,
  },
  boldText: { fontSize: 10, fontWeight: 700, textAlign: "center", marginBottom: 2 },
  sectionHead: { fontSize: 8, fontWeight: 700, padding: 3 },
  th: {
    fontSize: 9,
    fontWeight: 600,
    padding: 3,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000",
    textAlign: "center",
  },
  td: {
    fontSize: 8,
    padding: 3,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000",
  },
  right: { textAlign: "right" },
});

// ─── Reusable bordered cell ───────────────────────────────────────────────────
const Cell = ({ children, width, style, textStyle, right }) => (
  <View style={[styles.cell, { width }, style]}>
    <Text style={[right && styles.right, textStyle]}>{children}</Text>
  </View>
);

// ─── Party block (Billed To / Shipped To) ────────────────────────────────────
const PartyBlock = ({ title, party }) => (
  <View style={[{ width: "50%" }]}>
    <View style={[styles.cell, { padding: 0 }]}>
      <Text style={styles.sectionHead}>{title}</Text>
    </View>
    <Cell width="100%">Name : {party?.name}</Cell>
    <Cell width="100%">Address : {party?.address}</Cell>
    <Cell width="100%">GSTIN : {party?.gstin}</Cell>
    <View style={styles.row}>
      <Cell width="50%">STATE : {party?.state}</Cell>
      <Cell width="50%">STATE CODE : {party?.stateCode}</Cell>
    </View>
  </View>
);

// ─── Commodity columns config ─────────────────────────────────────────────────
const COMMODITY_COLS = [
  { header: "Sl No.", key: "slNo", w: "5%" },
  { header: "Commodity", key: "name", w: "25%" },
  { header: "HSN", key: "hsnCode", w: "7%" },
  { header: "Rate of Tax", key: "taxPercentage", w: "5%" },
  { header: "Qty", key: "quantity", w: "5%" },
  { header: "Rate", key: "unitPrice", w: "10%", right: true },
  { header: "Value", key: "grossValue", w: "10%", right: true },
  { header: "Disc", key: "discount", w: "5%", right: true },
  { header: "Taxable", key: "taxableValue", w: "10%", right: true },
  { header: "OPT", key: "opt", w: "8%", right: true },
  { header: "Total", key: "totalAmount", w: "10%", right: true },
];

// ─── Main component ───────────────────────────────────────────────────────────
export const CustomInvoicePdf = ({ data }) => {
  const { supplier, invoice, receiver, consignee, commodities, tax, totals } = data;

  return (
    <Document title={`Invoice ${invoice?.number || ""}`}>
      <Page size="A4" style={styles.page}>
        {/* ── Supplier header ── */}
        <View style={[styles.table, styles.tableFirst]}>
          <View style={styles.row}>
            <View style={styles.logoContainer}>
              <Image src={supplier.logoUrl ?? "/images/logo-round.jpg"} style={styles.logo} />
            </View>
            <Cell
              width="70%"
              style={{ textAlign: "center", minHeight: 70 }}
              textStyle={{ fontSize: 12, fontWeight: 700, paddingTop: 7 }}
            >
              {`${supplier?.name}\n${supplier?.address}\nMob: ${supplier?.mobile}`}
            </Cell>
          </View>
          <View style={styles.row}>
            <Cell width="40%" textStyle={{ fontWeight: 700 }}>
              GSTIN OF THE SUPPLIER
            </Cell>
            <Cell width="60%" textStyle={styles.boldText}>
              {supplier?.gstin}
            </Cell>
          </View>
          {/* Invoice No. / Date / State | Category + State Code | Copy types */}
          <View style={styles.row}>
            {/* Column 1+2: Invoice No, Date, State stacked */}
            <View style={{ width: "40%" }}>
              <View style={styles.row}>
                <Cell width="50%">INVOICE NO</Cell>
                <Cell width="50%" textStyle={{ fontWeight: 700 }}>
                  {invoice?.number}
                </Cell>
              </View>

              <View style={styles.row}>
                <Cell width="50%">DATE</Cell>
                <Cell width="50%" textStyle={{ fontWeight: 700 }}>
                  {invoice?.date ? new Date(invoice.date).toLocaleDateString("en-IN") : ""}
                </Cell>
              </View>

              <View style={styles.row}>
                <Cell width="50%">VEHICLE NO</Cell>
                <Cell width="50%" textStyle={{ fontWeight: 700 }}>
                  {invoice?.vehicleNumber || "-"}
                </Cell>
              </View>
            </View>

            {/* RIGHT 60% */}
            <View style={{ width: "60%", flexDirection: "row" }}>
              <Cell
                width="66.67%"
                style={{ justifyContent: "center" }}
                textStyle={{
                  ...styles.boldText,
                  marginBottom: 0,
                }}
              >
                {invoice?.category}
              </Cell>

              <View style={{ width: "33.33%" }}>
                <Cell width="100%">Original for Recipient</Cell>
                <Cell width="100%">Duplicate for Transporter</Cell>
                <Cell width="100%">Triplicate for Supplier</Cell>
              </View>
            </View>
          </View>
          {/* STATE ROW */}
          <View style={styles.row}>
            <Cell width="20%">STATE</Cell>
            <Cell width="20%">{supplier?.state}</Cell>

            <Cell width="20%">STATE CODE</Cell>
            <Cell width="40%" textStyle={{ fontWeight: 700 }}>
              {supplier?.stateCode}
            </Cell>
          </View>
        </View>
        {/* ── Billed To / Shipped To ── */}
        <View style={styles.table}>
          <View style={styles.row}>
            <PartyBlock title="DETAILS OF RECEIVER / BILLED TO" party={receiver} />
            <PartyBlock title="DETAILS OF CONSIGNEE / SHIPPED TO" party={consignee} />
          </View>
        </View>

        {/* ── Commodity table ── */}
        <View style={styles.table}>
          {/* Header row */}
          <View style={styles.row}>
            {COMMODITY_COLS.map(({ header, w }) => (
              <Text key={header} style={[styles.th, { width: w }]}>
                {header}
              </Text>
            ))}
          </View>
          {/* Data rows */}
          {commodities.map((c, rowIdx) => (
            <View style={[styles.row, { minHeight: commodities.length === 1 ? 100 : 20 }]} key={rowIdx}>
              {COMMODITY_COLS.map(({ key, w, right }) => (
                <Text key={key} style={[styles.td, right && styles.right, { width: w }]}>
                  {c[key]}
                </Text>
              ))}
            </View>
          ))}
        </View>

        {/* ── Totals + Bank ── */}
        <View style={styles.table}>
          {/* Row 1: Amount in Words | Tax breakdown */}
          <View style={styles.row}>
            {/* Left: 1 merged cell for amount in words + 3 cells for bank details = 4 rows, visually 5 */}
            <View style={{ width: "55%" }}>
              <Cell width="100%" style={{ minHeight: 38 }}>
                <Text style={{ fontWeight: 700 }}>Total Amount in Words: </Text>
                <Text>{totals?.amountInWords}</Text>
              </Cell>
              <Cell width="100%" textStyle={{ fontWeight: 700, textAlign: "center" }}>
                Bank details:
              </Cell>
              <Cell width="100%">
                Bank A/C No{"      "}: {supplier?.bankAccount}
              </Cell>
              <Cell width="100%">Bank Branch IFSC : {supplier?.ifsc}</Cell>
            </View>

            {/* Right: 5 fixed cells, no conditional rendering */}
            <View style={{ width: "45%", flexDirection: "column" }}>
              <View style={styles.row}>
                <Cell width="60%">Total Amount before tax</Cell>
                <Cell width="40%" textStyle={{ textAlign: "right" }}>
                  {totals?.beforeTax}
                </Cell>
              </View>
              <View style={styles.row}>
                <Cell width="60%">Total Tax</Cell>
                <Cell width="40%" textStyle={{ textAlign: "right" }}>
                  {totals?.totalTax}
                </Cell>
              </View>
              <View style={styles.row}>
                <Cell width="60%">CGST</Cell>
                <Cell width="40%" textStyle={{ textAlign: "right" }}>
                  {totals?.cgst}
                </Cell>
              </View>

              <View style={styles.row}>
                <Cell width="60%">SGST</Cell>
                <Cell width="40%" textStyle={{ textAlign: "right" }}>
                  {totals?.sgst}
                </Cell>
              </View>
              <View style={styles.row}>
                <Cell width="60%">Total Amount after tax</Cell>
                <Cell width="40%" textStyle={{ textAlign: "right", fontWeight: 700 }}>
                  {totals?.afterTax}
                </Cell>
              </View>
            </View>
          </View>

          {/* ── T&C + Seal row ── */}
          <View style={styles.row}>
            {/* Left: T&C + Seal — single cell, no internal splits */}
            <Cell width="50%" style={{ minHeight: 80 }}>
              <Text style={{ fontWeight: 700 }}>Terms & Conditions:</Text>
              {"\n"}
              <Text style={{ fontStyle: "italic" }}>Certified that the particulars given above are true and correct.</Text>
            </Cell>

            {/* Right: two sections inside single bordered view */}
            <View style={[styles.cell, { width: "50%", minHeight: 80, flexDirection: "row", borderRightWidth: 1 }]}>
              {/* Left section: Seal pinned to bottom */}
              <View style={{ width: "50%", justifyContent: "flex-end" }}>
                <Text>Seal</Text>
              </View>

              {/* Right section: For name at top, Authorized Signatory at bottom */}
              <View style={{ width: "50%", justifyContent: "space-between", alignItems: "flex-end" }}>
                <Text style={{ fontWeight: 700 }}>For {supplier?.name}</Text>
                <Text style={{ textAlign: "right" }}>Authorized Signatory{"\n"}(E & OE)</Text>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

// ─── Data mapper ──────────────────────────────────────────────────
export const getInvoicePdfPayload = (invoice, client, company) => {
  const amountBeforeTax = Number(invoice?.subtotal || 0);
  const totalInvoiceAmount = Number(invoice?.totalAmount || 0);
  const totalTaxAmount = Number(invoice?.taxDetails?.cgstAmount || 0) + Number(invoice?.taxDetails?.sgstAmount || 0);
  const cgstAmount = Number(invoice?.taxDetails?.cgstAmount || 0).toFixed(2);
  const sgstAmount = Number(invoice?.taxDetails?.sgstAmount || 0).toFixed(2);

  return {
    supplier: {
      name: company?.name,
      logoUrl: company?.logoFile?.url,
      address: [company?.primaryAddress?.locality, company?.primaryAddress?.state, company?.primaryAddress?.pinCode]
        .filter(Boolean)
        .join(", "),
      mobile: company?.primaryPhone,
      gstin: company?.taxDetails?.gstNumber,
      state: company?.primaryAddress?.state,
      stateCode: company?.primaryAddress?.stateCode,
      bankAccount: invoice?.bankDetails?.bankAccountNumber || "",
      ifsc: invoice?.bankDetails?.ifscCode || "",
    },
    invoice: {
      number: invoice?.invoiceNumber,
      date: invoice?.date,
      vehicleNumber: invoice?.vehicleNumber,
      buyerType: invoice?.buyerType || "B2B",

      category: `GST INVOICE ( ${invoice?.buyerType || "B2B"} )`,
    },
    receiver: {
      gstin: client?.taxId,
      name: client?.name,
      address: [client?.primaryAddress?.locality, client?.primaryAddress?.state, client?.primaryAddress?.pinCode]
        .filter(Boolean)
        .join(", "),
      state: client?.primaryAddress?.state,
      stateCode: client?.primaryAddress?.stateCode,
    },

    consignee: {
      gstin: client?.taxId,
      name: client?.name,
      address: [client?.primaryAddress?.locality, client?.primaryAddress?.state, client?.primaryAddress?.pinCode]
        .filter(Boolean)
        .join(", "),
      state: client?.primaryAddress?.state,
      stateCode: client?.primaryAddress?.stateCode,
    },
    commodities: invoice?.items?.map((item, index) => ({
      slNo: index + 1,
      name: item?.name,
      hsnCode: item?.hsnCode,
      taxPercentage: item?.taxPercentage,
      quantity: item?.quantity,
      unitPrice: item?.unitPrice,
      grossValue: item?.unitPrice * item?.quantity,
      discount: item?.discount || 0,
      taxableValue: item?.unitPrice * item?.quantity,
      opt: (Number(item?.totalAmount) - Number(item?.unitPrice * item?.quantity)).toFixed(2),
      totalAmount: item?.totalAmount,
    })),
    totals: {
      beforeTax: amountBeforeTax,
      afterTax: totalInvoiceAmount,
      totalTax: totalTaxAmount,
      cgst: cgstAmount,
      sgst: sgstAmount,
      amountInWords: `Rupees ${numWords(Math.round(totalInvoiceAmount))} only`,
    },
  };
};
